/**
 * DataSources API Service (TASK-017)
 *
 * API client functions for data source management.
 */
import apiClient from './client';
import type {
  DataSource,
  DataSourceFilters,
  PaginatedResponse,
  CreateDataSourcePayload,
  DataSourceType,
  ReindexResponse,
  ReindexAllResponse,
  DataSourceStats,
  DataSourceDocuments,
  FeedAutoUpdate,
  FeedAutoUpdatePayload,
} from '@/types/dataSource';

export const dataSourcesApi = {
  /**
   * List data sources with optional filters.
   */
  list: async (
    params?: DataSourceFilters
  ): Promise<PaginatedResponse<DataSource>> => {
    const { data } = await apiClient.get<PaginatedResponse<DataSource>>(
      '/api/v1/data-sources/',
      { params }
    );
    return data;
  },

  /**
   * Get a single data source by ID.
   */
  getById: async (id: string): Promise<DataSource> => {
    const { data } = await apiClient.get<DataSource>(
      `/api/v1/data-sources/${id}/`
    );
    return data;
  },

  /**
   * Create a new data source (WEB or WIKI).
   */
  create: async (payload: CreateDataSourcePayload): Promise<DataSource> => {
    const { data } = await apiClient.post<DataSource>(
      '/api/v1/data-sources/',
      payload
    );
    return data;
  },

  /**
   * Update an existing data source.
   */
  update: async (
    id: string,
    payload: Partial<CreateDataSourcePayload>
  ): Promise<DataSource> => {
    const { data } = await apiClient.patch<DataSource>(
      `/api/v1/data-sources/${id}/`,
      payload
    );
    return data;
  },

  /**
   * KAN-738: включить/выключить ночное обновление FEED-источника.
   */
  setAutoUpdate: async (id: string, value: FeedAutoUpdate): Promise<DataSource> => {
    const payload: FeedAutoUpdatePayload = { auto_update: value };
    const { data } = await apiClient.patch<DataSource>(
      `/api/v1/data-sources/${id}/`,
      payload
    );
    return data;
  },

  /**
   * Upload a file (Excel price list or e-commerce FEED) to create a data source.
   *
   * @param type - Optional source type. Omitted for Excel (server defaults to
   *   EXCEL); pass 'FEED' to upload a YML/XML/JSON product feed (TASK-270).
   */
  upload: async (
    file: File,
    name?: string,
    description?: string,
    isInformational?: boolean,
    type?: DataSourceType
  ): Promise<DataSource> => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) {
      formData.append('name', name);
    }
    if (description) {
      formData.append('description', description);
    }
    if (isInformational !== undefined) {
      formData.append('is_informational', isInformational ? 'true' : 'false');
    }
    if (type) {
      formData.append('type', type);
    }

    const { data } = await apiClient.post<DataSource>(
      '/api/v1/data-sources/upload/',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  /**
   * Delete a data source.
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/data-sources/${id}/`);
  },

  /**
   * Trigger reindexing of a data source.
   */
  reindex: async (id: string): Promise<ReindexResponse> => {
    const { data } = await apiClient.post<ReindexResponse>(
      `/api/v1/data-sources/${id}/reindex/?force=true`
    );
    return data;
  },

  /**
   * Trigger org-wide reindexing of all data sources (TASK-203).
   */
  reindexAll: async (): Promise<ReindexAllResponse> => {
    const { data } = await apiClient.post<ReindexAllResponse>(
      '/api/v1/data-sources/reindex-all/'
    );
    return data;
  },

  /**
   * Get documents for a data source.
   */
  getDocuments: async (id: string): Promise<DataSourceDocuments> => {
    const { data } = await apiClient.get<DataSourceDocuments>(
      `/api/v1/data-sources/${id}/documents/`
    );
    return data;
  },

  /**
   * Get statistics for a data source.
   */
  getStats: async (id: string): Promise<DataSourceStats> => {
    const { data } = await apiClient.get<DataSourceStats>(
      `/api/v1/data-sources/${id}/stats/`
    );
    return data;
  },
};

export default dataSourcesApi;
